import React, { useState, useEffect } from "react";
import { Box, Link } from "@mui/material";
import { useLocation } from "react-router-dom";
import Logo from "../assets/images/logoNew.png";
import { colors, fonts, gradients, shadows, layout } from "../theme/tokens";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about-us" },
  { label: "Services", href: "/services" },
  { label: "Contact", href: "/contact-us" },
];

const Navbar = () => {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isActive = (href) =>
    href === "/" ? pathname === "/" : pathname.startsWith(href);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  return (
    <>
      {/* ===== Header ===== */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          zIndex: 100,
          backgroundColor: colors.surface,
          borderBottom: `1px solid ${colors.line}`,
        }}
      >
        <Box
          sx={{
            maxWidth: layout.maxWidth,
            mx: "auto",
            px: { xs: "20px", md: "28px" },
            py: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "24px",
            boxSizing: "border-box",
          }}
        >
          {/* Logo */}
          <Link href="/" sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src={Logo}
              alt="Argyle Consulting"
              sx={{ height: "40px", width: "auto", display: "block" }}
            />
          </Link>

          {/* Desktop nav */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: "34px",
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                underline="none"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "4px",
                  fontFamily: fonts.body,
                  fontWeight: 600,
                  fontSize: "15px",
                  color: colors.brandNavy,
                  transition: "color .25s ease",
                  "&:hover": { color: colors.amber },
                }}
              >
                {link.label}
                <Box
                  sx={{
                    width: "18px",
                    height: "2px",
                    borderRadius: "2px",
                    backgroundColor: colors.amber,
                    opacity: isActive(link.href) ? 1 : 0,
                    transition: "opacity .25s ease",
                  }}
                />
              </Link>
            ))}
          </Box>

          {/* Desktop contact + CTA */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: "18px",
            }}
          >
            <Link
              href="tel:+2348033910386"
              underline="none"
              sx={{
                fontFamily: fonts.body,
                fontWeight: 600,
                fontSize: "14px",
                color: colors.steelBlue,
              }}
            >
              +234 803 391 0386
            </Link>
            <Link
              href="/contact-us"
              underline="none"
              sx={{
                background: gradients.amber,
                color: "#fff",
                fontFamily: fonts.body,
                fontWeight: 700,
                fontSize: "14px",
                px: "22px",
                py: "11px",
                borderRadius: "40px",
                boxShadow: shadows.amberBtn,
                transition: "transform .25s ease",
                "&:hover": { transform: "translateY(-1px)" },
              }}
            >
              Book a Consultation
            </Link>
          </Box>

          {/* Mobile hamburger */}
          <Box
            component="button"
            aria-label="Open menu"
            onClick={() => setMenuOpen(true)}
            sx={{
              display: { xs: "flex", md: "none" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "5px",
              width: "46px",
              height: "46px",
              borderRadius: "12px",
              border: "none",
              backgroundColor: colors.brandNavy,
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                width: "20px",
                height: "2px",
                borderRadius: "2px",
                backgroundColor: "#fff",
              }}
            />
            <Box
              sx={{
                width: "20px",
                height: "2px",
                borderRadius: "2px",
                backgroundColor: "#fff",
              }}
            />
            <Box
              sx={{
                width: "14px",
                height: "2px",
                borderRadius: "2px",
                backgroundColor: colors.amber,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* ===== Mobile menu overlay ===== */}
      {menuOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100dvh",
            boxSizing: "border-box",
            zIndex: 110,
            background: gradients.navy,
            display: "flex",
            flexDirection: "column",
            px: "22px",
            pt: "20px",
            pb: "28px",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            animation: "menuFade .3s ease both",
            "@keyframes menuFade": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
            "@keyframes menuItemIn": {
              from: { opacity: 0, transform: "translateY(14px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Amber glow */}
          <Box
            sx={{
              position: "absolute",
              top: "-120px",
              right: "-90px",
              width: "320px",
              height: "320px",
              borderRadius: "50%",
              background:
                "radial-gradient(circle,rgba(246,166,35,.18),transparent 65%)",
              pointerEvents: "none",
            }}
          />

          {/* Header: wordmark + close */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontFamily: fonts.display,
                  fontWeight: 700,
                  fontSize: "22px",
                  color: "#fff",
                  letterSpacing: ".02em",
                }}
              >
                ARGYLE
                <Box
                  component="span"
                  sx={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: colors.amberDot,
                    ml: "3px",
                  }}
                />
              </Box>
              <Box
                sx={{
                  fontFamily: fonts.display,
                  fontSize: "10px",
                  letterSpacing: ".32em",
                  color: colors.footerLabel,
                  mt: "2px",
                }}
              >
                CONSULTING LTD
              </Box>
            </Box>
            <Box
              component="button"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              sx={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,.14)",
                backgroundColor: "rgba(255,255,255,.06)",
                color: "#fff",
                fontSize: "18px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background .2s ease",
                "&:hover": { backgroundColor: "rgba(255,255,255,.12)" },
              }}
            >
              ✕
            </Box>
          </Box>

          {/* Nav links */}
          <Box
            component="nav"
            sx={{
              position: "relative",
              mt: "36px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {navLinks.map((link, i) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  underline="none"
                  onClick={() => setMenuOpen(false)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    py: "17px",
                    borderBottom:
                      i === navLinks.length - 1
                        ? "none"
                        : "1px solid rgba(255,255,255,.08)",
                    animation: `menuItemIn .45s ${0.06 * i + 0.08}s cubic-bezier(.2,.7,.2,1) both`,
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: fonts.display,
                      fontWeight: 700,
                      fontSize: "13px",
                      minWidth: "26px",
                      color: active ? colors.amberDot : "rgba(255,255,255,.35)",
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      fontFamily: fonts.display,
                      fontWeight: 600,
                      fontSize: "28px",
                      lineHeight: 1.1,
                      color: active ? colors.amberDot : "#fff",
                    }}
                  >
                    {link.label}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "20px",
                      color: active ? colors.amberDot : "rgba(255,255,255,.35)",
                    }}
                  >
                    →
                  </Box>
                </Link>
              );
            })}
          </Box>

          {/* CTA */}
          <Link
            href="/contact-us"
            underline="none"
            onClick={() => setMenuOpen(false)}
            sx={{
              position: "relative",
              mt: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              background: gradients.amber,
              color: "#fff",
              fontFamily: fonts.body,
              fontWeight: 700,
              fontSize: "16px",
              py: "16px",
              borderRadius: "44px",
              boxShadow: shadows.amberBtn,
              animation: "menuItemIn .45s .34s cubic-bezier(.2,.7,.2,1) both",
            }}
          >
            Book a Consultation
            <Box component="span" sx={{ fontSize: "18px" }}>
              →
            </Box>
          </Link>

          {/* Contact rows */}
          <Box
            sx={{
              position: "relative",
              mt: "auto",
              pt: "28px",
              borderTop: "1px solid rgba(255,255,255,.08)",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {[
              {
                href: "tel:+2348033910386",
                label: "+234 803 391 0386",
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M6.5 4h-2A1.5 1.5 0 0 0 3 5.6C3 13 11 21 18.4 21A1.5 1.5 0 0 0 20 19.5v-2a1.5 1.5 0 0 0-1.2-1.5l-2.6-.5a1.5 1.5 0 0 0-1.5.7l-.5.8a11 11 0 0 1-4.9-4.9l.8-.5a1.5 1.5 0 0 0 .7-1.5l-.5-2.6A1.5 1.5 0 0 0 6.5 4Z"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                ),
              },
              {
                href: "mailto:info@argyleconsulting.org",
                label: "info@argyleconsulting.org",
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3"
                      y="5"
                      width="18"
                      height="14"
                      rx="2"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                    <path
                      d="m4 7 8 6 8-6"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                ),
              },
              {
                href: null,
                label: "Lagos State, Nigeria",
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 21s7-5.5 7-11a7 7 0 1 0-14 0c0 5.5 7 11 7 11Z"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                    <circle
                      cx="12"
                      cy="10"
                      r="2.5"
                      stroke={colors.amberDot}
                      strokeWidth="1.8"
                    />
                  </svg>
                ),
              },
            ].map((row) => {
              const content = (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <Box
                    sx={{
                      width: "34px",
                      height: "34px",
                      borderRadius: "10px",
                      background: "rgba(255,255,255,.07)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: "none",
                    }}
                  >
                    {row.icon}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: fonts.body,
                      fontSize: "14px",
                      color: "rgba(255,255,255,.8)",
                    }}
                  >
                    {row.label}
                  </Box>
                </Box>
              );
              return row.href ? (
                <Link key={row.label} href={row.href} underline="none">
                  {content}
                </Link>
              ) : (
                <Box key={row.label}>{content}</Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default Navbar;
